import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import T from './theme';

// iOS Safari — especially a home-screen web clip — fires resize (and sometimes
// scroll) as the visual viewport settles right after a fixed overlay appears.
// Closing on those events opened and vanished the bubble in the same tick, which
// read as "the pop-up does not come up". Ignore viewport events for this long
// after opening; after that a real scroll/resize still dismisses.
export const VIEWPORT_GRACE_MS = 400;

function fineHover() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

function place(anchor, align) {
  const r = anchor.getBoundingClientRect();
  const width = Math.min(260, Math.max(168, window.innerWidth - 16));
  let left = align === 'end' ? r.right - width : r.left;
  left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
  const gap = 6;
  const estimatedHeight = 200;
  let top = r.bottom + gap;
  if (top + estimatedHeight > window.innerHeight - 8 && r.top > estimatedHeight) {
    top = Math.max(8, r.top - estimatedHeight - gap);
  }
  return { top, left, width };
}

export default function InfoBubble({ label, align = 'start', children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const openedAtRef = useRef(0);
  const id = useId();

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) {
      setPos(null);
      return;
    }
    // Measure the trigger, not the tooltip: the rect is in viewport coordinates,
    // which is what the portaled (body-level, untransformed) tooltip needs.
    setPos(place(wrapRef.current, align));
  }, [open, align, children]);

  useEffect(() => {
    if (!open) return undefined;
    openedAtRef.current = Date.now();
    const onPointerDown = (e) => {
      // The tooltip is portaled to document.body, so it is no longer inside the
      // wrap. Both the trigger and the tooltip count as "inside".
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (tipRef.current && tipRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onViewport = () => {
      if (Date.now() - openedAtRef.current < VIEWPORT_GRACE_MS) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onViewport, true);
    window.addEventListener('resize', onViewport);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onViewport, true);
      window.removeEventListener('resize', onViewport);
    };
  }, [open]);

  const tip = (
    <span
      id={id}
      ref={tipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos ? pos.top : 8,
        left: pos ? pos.left : 8,
        width: pos ? pos.width : Math.min(260, 280),
        // Portaled to body, so this only has to clear the app chrome, not a
        // stacking context inside a card.
        zIndex: 1000,
        padding: '10px 12px',
        background: T.color.bg,
        color: T.color.text,
        border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.sm,
        boxShadow: T.shadow.modal,
        fontSize: T.font.xs,
        fontWeight: 400,
        lineHeight: 1.45,
        textAlign: 'left',
        whiteSpace: 'normal',
      }}
    >
      {children}
    </span>
  );

  return (
    <span
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline' }}
      onMouseEnter={() => {
        if (fineHover()) setOpen(true);
      }}
      onMouseLeave={() => {
        if (fineHover()) setOpen(false);
      }}
    >
      <button
        type="button"
        className="info-bubble-trigger"
        aria-expanded={open}
        aria-controls={id}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (fineHover()) {
            setOpen(true);
            return;
          }
          setOpen((was) => !was);
        }}
      >
        {label}
      </button>
      {/* A dnd-kit SortableCard ancestor carries a CSS transform, which would
          make position:fixed resolve against the card instead of the viewport.
          Render at the body so the computed coords mean what they say. */}
      {open && typeof document !== 'undefined' ? createPortal(tip, document.body) : null}
    </span>
  );
}
